import Grid from "@mui/material/Grid"
import {observable} from "mobx"
import {observer} from "mobx-react"
import React from "react"
import {H1Text} from "../../../../styles/AvantTypography"
import {theme} from "../../../../styles/MuiThemes"
import {authStore} from "../../../common/authentication/AuthStore"
import {groupMasterStore} from "../../../common/group/GroupMasterStore"
import {GroupInfo} from "../../../common/group/models/GroupInfo"
import {AvantCircularProgress} from "../../../common/loaders/AvantCircularProgress"
import {productStore} from "../../../common/products/ProductStore"
import {takeSearchStore} from "../../../common/take/TakeSearchStore"
import {AvantExpansionPanel} from "../../../generic-components/AvantExpansionPanel"
import {BreadcrumbHeader} from "../../common/components/Breadcrumbs"
import {FilterAndPrintOptions} from "../../common/components/FilterAndPrintOptions"
import {LevelKeyContent} from "../../common/components/level-key/LevelKeyContent"
import {PrintOptions} from "../../common/components/PrintViewContainer"
import {BlueReportType} from "../../common/models/BlueReportType"
import {GenericReportContainerProps} from "../../common/models/GenericReportProps"
import {ReportFilters} from "../../common/models/ReportFilters"
import {reportsStore} from "../../common/stores/ReportStore"
import {ReportSummaryGraphs} from "../models/ReportSummaryGraphs"
import {DistrictTableContainer} from "./district-school-table/DistrictTable"
import {SchoolTableContainer} from "./district-school-table/SchoolTable"
import moment from "moment"
import {ReportGraphFilters} from "../../common/models/ReportGraphFilters"
import AccordionDetails from "@mui/material/AccordionDetails"
import {useEffect} from "react";
import {useReportUrlFilters} from "../../common/hooks/useReportUrlFilters"
import {ReportGraphSummary} from "./district-school-table/ReportGraphSummary"
import {TestTakerSearchLink} from "../../common/components/TestTakerSearch/components/TestTakerSearchLink"
import {Stack} from "@mui/material"

export const DistrictReportPage = () => {

    useEffect(() => {
        document.title = "Avant | District Report"
    }, [])

    return <DistrictOrSchoolReportContainer isDistrict={true} filters={useReportUrlFilters()}/>
}

export const SchoolReportPage = () => {

    useEffect(() => {
        document.title = "Avant | School Report"
    })

    return <DistrictOrSchoolReportContainer isDistrict={false} filters={useReportUrlFilters()}/>
}

interface DistrictOrSchoolReportContainerProps extends GenericReportContainerProps {
    isDistrict: boolean
}

@observer
class DistrictOrSchoolReportContainer extends React.Component<DistrictOrSchoolReportContainerProps> {

    constructor(props: DistrictOrSchoolReportContainerProps) {
        super(props)

        // Make sure the first render cycle doesn't have data from the previously loaded report.
        reportsStore.reportTableData = undefined
        reportsStore.reportGraphData = undefined
    }

    componentDidMount(): void {
        // log.debug("component did mount of district/school report container, id " + this.props.filters.groupId)
        this.searchReportData(this.props)
    }

    componentDidUpdate(prevProps: Readonly<DistrictOrSchoolReportContainerProps>, prevState: Readonly<{}>, snapshot?: any) {
        const {filters} = this.props
        if (filters.groupId !== prevProps.filters.groupId
            || !filters.startDate.isSame(prevProps.filters.startDate)
            || !filters.endDate.isSame(prevProps.filters.endDate)
            || filters.assessmentStart !== prevProps.filters.assessmentStart
            || filters.recentActivity !== prevProps.filters.recentActivity
        ) {
            this.searchReportData(this.props)
        }
    }

    getQuery = (props: DistrictOrSchoolReportContainerProps): ReportGraphFilters => {
        const {filters} = props
        const {groupId, productId, startDate, endDate, assessmentStart, recentActivity} = filters

        //make copies so we don't change state
        const startDateStartOfDay = moment(startDate).startOf("day").valueOf()
        const endDateEndOfDay = moment(endDate).endOf("day").valueOf()

        reportsStore.searchFilters = {groupId, productId, assessmentStart, recentActivity, startDate, endDate}

        return {
            groupId,
            productId,
            start: startDateStartOfDay,
            end: endDateEndOfDay,
            searchByStartTimes: assessmentStart,
            searchByMostRecentActivityTimes: recentActivity
        }

    }

    searchReportData = (props: DistrictOrSchoolReportContainerProps) => {
        const {filters, isDistrict} = props
        const {groupId} = filters

        takeSearchStore.instances = []
        takeSearchStore.downloadComplete = false

        const productId = productStore.loginProduct?.productId || -1
        if (isDistrict) {
            if (authStore.auth !== undefined && authStore.auth !== null &&
                authStore.auth.ssoId !== null &&  authStore.auth.ssoId !== undefined) {
                reportsStore.findSSODistrictReport(groupId, productId)
            } else {
                reportsStore.findDistrictReportData(groupId)
            }
        } else {
            if (authStore.auth !== undefined && authStore.auth !== null &&
                authStore.auth.ssoId !== null &&  authStore.auth.ssoId !== undefined) {
                reportsStore.findSSOSchoolReportData(groupId, productId)
            } else {
                reportsStore.findSchoolReportData(groupId)
            }
        }
        const query = this.getQuery(props)
        reportsStore.findReportGraphs(query)
        reportsStore.findGroupsWithData(query)
        reportsStore.resetDownloads()
        groupMasterStore.groupInfo = undefined
        groupMasterStore.findDistrictReportGroupInfo(groupId)
    }

    render() {
        if (reportsStore.reportGraphData == null || groupMasterStore.groupInfo == null) {
            return <AvantCircularProgress/>
        }

        return (
            <DistrictOrSchoolReportView
                graphData={reportsStore.reportGraphData!}
                groupInfo={groupMasterStore.groupInfo}
                district={this.props.isDistrict}
                filters={this.props.filters}
                query={this.getQuery(this.props)}
            />
        )
    }
}

export interface DistrictOrSchoolReportViewProps {
    graphData: ReportSummaryGraphs
    groupInfo: GroupInfo
    district: boolean
    filters: ReportFilters
    query?: ReportGraphFilters
    printOptions?: PrintOptions
}

@observer
export class DistrictOrSchoolReportView extends React.Component<DistrictOrSchoolReportViewProps> {

    @observable
    sidebarOpen: boolean = false

    render() {
        const {groupInfo, query, district, filters, printOptions} = this.props

        return (
            <Stack gap={1.25}>
                {printOptions == null && <BreadcrumbHeader groupInfo={groupInfo} filters={filters}/>}
                <Grid container sx={{justifyContent: "space-between", alignItems: "flex-end"}}>
                    <H1Text>
                        {groupInfo.name ?? ""}
                    </H1Text>
                    {printOptions ? (
                            <LevelKeyContent/>
                        ) : (
                        <FilterAndPrintOptions
                            reportType={district ? BlueReportType.DISTRICT : BlueReportType.SCHOOL}
                            filters={filters}
                            query={query!!}
                        />
                    )}
                </Grid>
                {!printOptions && <TestTakerSearchLink groupId={filters.groupId}/>}
                {printOptions !== PrintOptions.LIST && <ReportGraphSummary printOptions={printOptions}/>}
                {printOptions !== PrintOptions.SUMMARY && (
                    <AvantExpansionPanel
                        expanded={printOptions != null ? true : undefined}
                        sx={{margin: 0, breakBefore: "page"}}
                        title={district ? "School/Location List" : "Testing Groups"}
                    >
                        <AccordionDetails
                            style={{
                                width: "100%",
                                display: "inherit",
                                padding: theme.spacing(2, 0)
                            }}
                        >
                            {
                                district ? (
                                    <DistrictTableContainer
                                        reportData={reportsStore.districtReportData}
                                        filters={filters}
                                        printing={printOptions != null}
                                    />
                                ) : (
                                    <SchoolTableContainer
                                        reportData={reportsStore.schoolReportData}
                                        filters={filters}
                                        printing={printOptions != null}
                                    />
                                )
                            }
                        </AccordionDetails>
                    </AvantExpansionPanel>
                )}
            </Stack>
        )
    }
}